import React, { useEffect, useRef } from 'react';

interface AutoCompleteItem {
  key: string | number;
  display: string;
}

interface AutoCompleteProps {
  items?: AutoCompleteItem[];
  onSelect?: (item: AutoCompleteItem) => void;
  onClose?: () => void;
}

export default function AutoComplete({
  items,
  onSelect,
  onClose
}: AutoCompleteProps): React.ReactElement {
  const autoCompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (autoCompleteRef.current && !autoCompleteRef.current.contains(event.target as Node)) {
        onClose && onClose();
      }
    };

    const handleEscapePress = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose && onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapePress);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapePress);
    };
  }, [onClose]);

  return (
    <div ref={autoCompleteRef} className={`absolute z-10 ${(!items || !items.length) && 'hidden'}`}>
      <div className="mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto">
        {items &&
          items.map(item => (
            <div
              key={item.key}
              className="px-4 py-3 cursor-pointer hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0 transition-colors focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:ring-inset"
              onClick={() => onSelect && onSelect(item)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSelect && onSelect(item);
                }
              }}
              tabIndex={0}
              role="option"
              aria-selected={false}
            >
              <span className="text-sm text-gray-900 font-medium">{item.display}</span>
            </div>
          ))}
      </div>
    </div>
  );
}
